import {AES, enc} from 'crypto-js';
import moment from 'moment';

export const setItem = (type, key, value) => {
  try {
    type.setItem(
      key,
      AES.encrypt(value, process.env.REACT_APP_SECRET).toString(),
    );
    return true;
  } catch (error) {
    console.error('setItem error:', error);
    return false;
  }
};

export const getItem = (type, key, defaultValue) => {
  try {
    const encryptedValue = type.getItem(key);
    if (!encryptedValue) {
      return defaultValue;
    }
    const decryptedValue = AES.decrypt(
      encryptedValue,
      process.env.REACT_APP_SECRET,
    ).toString(enc.Utf8);
    return decryptedValue;
  } catch (error) {
    console.error('getItem error:', error);
    return defaultValue;
  }
};

export const removeItem = (type, key) => {
  type.removeItem(key);
};

export const removeAll = (type) => {
  type.clear();
};

export const hasAccess = (accessNumber) => {
  return getItem(localStorage, 'resource_access')
    ?.split(',')
    ?.includes(accessNumber?.toString());
};

export const isColumnDateLessThanCurrentDate = (myDate) => {
  return moment(myDate).isBefore(moment().format('YYYY-MM-DD'));
};

export const getWeeksBetweenTwoDates = (startDate, endDate) => {
  startDate = moment(startDate);
  endDate = moment(endDate);
  const weeks = [];

  let currentWeek = moment(startDate).startOf('week');
  while (currentWeek <= endDate) {
    const endDateOfWeek = moment(currentWeek).endOf('week');

    weeks.push({
      id: crypto.randomUUID(),
      name: `${currentWeek.format('YYYY-MM-DD')} to ${endDateOfWeek.format(
        'YYYY-MM-DD',
      )}`,
      startDate: currentWeek.format('YYYY-MM-DD'),
      endDate: endDateOfWeek.format('YYYY-MM-DD'),
    });
    currentWeek = moment(currentWeek).add(1, 'week').startOf('week');
  }

  return weeks;
};

export const findWeekFromDate = (currentDate, weeks) => {
  currentDate = moment(currentDate);

  const currentWeekObj = weeks.find((week) => {
    return moment(currentDate).isBetween(
      moment(week.startDate),
      moment(week.endDate),
      null,
      '[]',
    );
  });

  return currentWeekObj;
};

export const isAlphaNumericValue = (value) => {
  return value && /^(?=.*[a-zA-Z])(?=.*[0-9]).+$/.test(value);
};

export const getHierarchyByTerminalPoint = (hierarchyTree, ffTerminalPoint) => {
  const hierarchy = [];

  const fn = (hierarchyTree, ffTerminalPoint) => {
    for (let i = 0; i < hierarchyTree.length; i++) {
      if (hierarchyTree[i].id >= ffTerminalPoint)
        hierarchy.push({
          id: hierarchyTree[i].id,
          name: hierarchyTree[i].name,
          parent: hierarchyTree[i].parent,
          parent_name: hierarchyTree[i].parent_name,
        });

      if (hierarchyTree[i].child?.length) {
        fn(hierarchyTree[i].child, ffTerminalPoint);
      }
    }
  };

  fn(hierarchyTree, ffTerminalPoint);

  return hierarchy;
};

export const removeEmptyFields = (obj) => {
  if (Array.isArray(obj)) {
    // Handle arrays
    for (let i = obj.length - 1; i >= 0; i--) {
      if (
        obj[i] === null ||
        obj[i] === undefined ||
        obj[i] === '' ||
        (Array.isArray(obj[i]) && obj[i].length === 0)
      ) {
        obj.splice(i, 1);
      } else if (typeof obj[i] === 'object') {
        // Recursively check and remove empty fields in nested objects and arrays
        removeEmptyFields(obj[i]);
        // If the nested object or array is empty after removing fields, delete the element
        if (
          (typeof obj[i] === 'object' && Object.keys(obj[i]).length === 0) ||
          (Array.isArray(obj[i]) && obj[i].length === 0)
        ) {
          obj.splice(i, 1);
        }
      }
    }
  } else if (typeof obj === 'object') {
    // Handle objects
    for (const key in obj) {
      if (
        obj[key] === null ||
        obj[key] === undefined ||
        obj[key] === '' ||
        (Array.isArray(obj[key]) && obj[key].length === 0)
      ) {
        delete obj[key];
      } else if (typeof obj[key] === 'object') {
        // Recursively check and remove empty fields in nested objects and arrays
        removeEmptyFields(obj[key]);
        // If the nested object or array is empty after removing fields, delete the key
        if (
          (typeof obj[key] === 'object' &&
            Object.keys(obj[key]).length === 0) ||
          (Array.isArray(obj[key]) && obj[key].length === 0)
        ) {
          delete obj[key];
        }
      }
    }
  }
  return obj;
};

export const getScheduleStatusFromCurrentDate = (fromDate, toDate) => {
  const fromDateObj = new Date(fromDate);
  const toDateObj = toDate ? new Date(toDate) : null;
  const currentObj = new Date();

  if (toDateObj && currentObj > toDateObj) {
    return 'Completed';
  } else if (currentObj < fromDateObj) {
    return 'Scheduled';
  } else {
    return 'Running';
  }
};

export const validateFileSize = (file) => {
  const fileSize = file.size / 1024;
  return fileSize <= 500;
};

export const cellData = (data) => (data ? <>{data}</> : <>-</>);

export const CHART_BUILDING_DATA = [
  'Count',
  'Count Unique Values',
  'List Unique Values',
  'Sum',
  'Integer Sum',
  'Average',
  'Median',
  'Sample Variance',
  'Sample Standard Deviation',
  'Minimum',
  'Maximum',
  'First',
  'Last',
  'Sum over Sum',
  'Sum as Fraction of Total',
  'Sub as Fraction of Rows',
  'Sum as Fraction of Columns',
  'Count as Fraction of Total',
  'Count as Fraction of Rows',
  'Count as Fraction of Columns',
];

export const CHART_TYPE = [
  'Grouped Columns Chart',
  'Grouped Column Chart',
  'Stacked Column Chart',
  'Grouped Bar Chart',
  'Stacked Bar Chart',
  'Line Chart',
  'Dot Chart',
  'Area Chart',
  'Scatter Chart',
  'Multiple Pie Chart',
  'Table',
  'Table Heatmap',
  'Table Col Heatmap',
  'Table Row Heatmap',
  'Exportable TSV',
];

export const getValueByKey = (obj, targetKey) => {
  let result;

  if (typeof obj !== 'object') throw new Error('Provide a valid object');

  function search(obj, key) {
    if (!obj || result) return; // Stop searching if result is found or if obj is null

    for (const prop in obj) {
      if (prop === key) {
        result = obj[prop];
        return;
      } else if (typeof obj[prop] === 'object') {
        search(obj[prop], key); // Recursively search nested objects
      }
    }
  }

  search(obj, targetKey);
  return result;
};
